import ResizableScene from "../core/ResizableScene";
import F84Game from "../Game";
import ActionQueue from "../utils/ActionQueue";
import { Player } from "./Player";
import { BGLayer } from "./ScrollingBG";

export class CameraController {
    scene: ResizableScene;
    camera: Phaser.Cameras.Scene2D.Camera;
    mode?: CameraMode;

    scale = 1;
    targetScale = 1;
    offsetX = 0;
    targetOffsetX = 0;
    offsetY = 0;
    targetOffsetY = 0;
    shakeX = 0;

    // not accurate unless you query during preRender
    leftBound: number;
    rightBound: number;

    constructor(scene: ResizableScene) {
        this.scene = scene;
        this.camera = scene.cameras.main;

        this.scene.input.keyboard.on("keydown-E", () => this.debug());
        this.calculateBounds();
    }

    setMode(mode: CameraMode): void {
        this.mode?.finish();
        this.mode = mode;
        mode.start();
    }

    update(dt: number): void {
        this.scale -= (this.scale - this.targetScale) * 1 * dt;
        const dpiZoom = this.scene.zoomScale;
        this.camera.zoom = this.scale * dpiZoom;
        this.offsetX -= (this.offsetX - this.targetOffsetX) * 1 * dt;
        this.offsetY -= (this.offsetY - this.targetOffsetY) * 1 * dt;
        this.mode?.update(dt);
        this.camera.scrollX += this.shakeX;
        //kludge for recap camera control
        const recapXBounds = 443030; //stage.x + this.camController.offsetX
        const recapYBounds = -231; //stage.y + this.camController.offsety
        if(this.camera.scrollX > recapXBounds){
            this.camera.scrollX = recapXBounds + 7;
        }
        this.constrainCamera();
    }

    constrainCamera(): void {
        const xMin = 220;
        const yMin = 1080 - 5000;
        const yMax = 1080;

        const dpiZoom = this.scene.zoomScale;
        const height = this.camera.displayHeight * dpiZoom;

        if (this.camera.scrollX < xMin) this.camera.scrollX = xMin;
        if (this.camera.scrollY + height > yMax) this.camera.scrollY = yMax - height;
        if (this.camera.scrollY < yMin) this.camera.scrollY = yMin;
    }

    // with the dpi/canvas scaling, camera.worldView gives wrong results. use this for correct ones
    getRealWorldView(): Phaser.Geom.Rectangle {
        const dpiZoom = this.scene.zoomScale;
        const width = this.camera.displayWidth * dpiZoom;
        const height = this.camera.displayHeight * dpiZoom;
        return new Phaser.Geom.Rectangle(this.camera.scrollX, this.camera.scrollY, width, height);
    }

    preRender(): void {
        this.calculateBounds();
    }

    calculateBounds(): void {
        this.leftBound = this.getLeftBound();
        this.rightBound = this.getRightBound();
    }

    clampCamera(): void {
        this.camera.scrollX = Math.max(this.camera.scrollX, 0);
        this.camera.scrollY = Math.min(this.camera.scrollY, 1080 - 1080);
    }

    follow(target: Player): void {
        this.setMode(new CameraFollowMode(this, target));
    }

    end(stageLayer: BGLayer, stage: Phaser.GameObjects.Image): void {
        this.setMode(new CameraEndMode(this, stageLayer, stage));
    }

    debug(): void {
        this.setMode(new CameraDebugMode(this));
        // this.camera.scrollX = 19555;
        // this.camera.scrollY = 2000;
    }

    shake(dist = 12): void {
        const duration = 90;
        const queue = new ActionQueue();
        queue.add(complete => {
            this.scene.tweens.add({
                targets: this,
                shakeX: { from: 0, to: dist },
                ease: Phaser.Math.Easing.Sine.Out,
                duration: duration / 2,
                onComplete: complete,
            });
        });
        queue.add(complete => {
            this.scene.tweens.add({
                targets: this,
                shakeX: { from: dist, to: -dist },
                ease: Phaser.Math.Easing.Sine.Out,
                yoyo: true,
                repeat: 3,
                duration: duration / 2,
                onComplete: complete,
            });
        });
        queue.add(complete => {
            this.scene.tweens.add({
                targets: this,
                shakeX: 0,
                ease: Phaser.Math.Easing.Sine.In,
                duration: duration / 2,
                onComplete: complete,
            });
        });
        queue.start();
    }

    getLeftBound(): number {
        return this.getRealWorldView().left;
    }

    getRightBound(): number {
        return this.getRealWorldView().right;
    }
}

class CameraMode {
    scene: ResizableScene;
    camController: CameraController;
    camera: Phaser.Cameras.Scene2D.Camera;
    events: Phaser.Events.EventEmitter;

    constructor(camController: CameraController) {
        this.scene = camController.scene;
        this.camera = camController.camera;
        this.camController = camController;
        this.events = new Phaser.Events.EventEmitter();
    }

    start(): void {
        return;
    }

    finish(): void {
        return;
    }

    update(_dt: number): void {
        return;
    }
}


class CameraFollowMode extends CameraMode {
    target: Player;
    constructor(camController: CameraController, target: Player) {
        super(camController);
        this.target = target;
    }

    update(dt: number): void {
        this.camController.targetOffsetY = Math.max((this.target.planckBody.velocity.y + 5) * 30, 0);
        this.camera.centerOn(this.target.x + this.camController.offsetX, this.target.y + this.camController.offsetY);
    }
}

class CameraEndMode extends CameraMode {
    target: Phaser.Math.Vector2;

    constructor(camController: CameraController, stageLayer: BGLayer, stage: Phaser.GameObjects.Image) {
        super(camController);
        this.target = stageLayer.getLocalTransformMatrix().transformPoint(stage.x + stage.width / 2, stage.y - 300, new Phaser.Math.Vector2()) as Phaser.Math.Vector2;
    }

    update(dt: number): void {
        this.camera.centerOn(this.target.x + this.camController.offsetX, this.target.y + this.camController.offsetY);
    }
}

class CameraDebugMode extends CameraMode {
    keys: Phaser.Types.Input.Keyboard.CursorKeys;

    constructor(camController: CameraController) {
        super(camController);
        this.keys = this.scene.input.keyboard.createCursorKeys();
    }

    update(dt: number): void {
        if (this.keys.left.isDown) this.camera.scrollX -= 1600 * dt;
        if (this.keys.right.isDown) this.camera.scrollX += 1600 * dt;
        if (this.keys.up.isDown) this.camera.scrollY -= 1600 * dt;
        if (this.keys.down.isDown) this.camera.scrollY += 1600 * dt;
    }
}
